import { PlanetName, ZodiacSign, NodeConvention, CalculationContext } from '../types/kundali';
import { calculateLivePlanetaryEphemeris, ZODIAC_SIGNS } from './vedicEngine';
import { formatDate } from './dateUtils';

export interface PlanetaryTransitPeriod {
  planet: PlanetName;
  sign: ZodiacSign;
  signIndex: number;
  startDate: Date;
  endDate: Date;
  startDateFormatted: string;
  endDateFormatted: string;
  durationDays: number;
  durationFormatted: string;
  daysElapsed: number;
  daysRemaining: number;
  progressPercentage: number;
  isRetrograde: boolean;
  degreeFormatted: string;
  // Upcoming Transit (Next Ingress & Full Next Period From/To) Information
  nextSign: ZodiacSign;
  nextSignIndex: number;
  nextIngressDate: Date;
  nextIngressDateFormatted: string;
  nextPeriodStartDate: Date;
  nextPeriodStartDateFormatted: string; // From Date
  nextPeriodEndDate: Date;
  nextPeriodEndDateFormatted: string;   // To Date
  nextDurationDays: number;
  nextDurationFormatted: string;
  daysUntilNextIngress: number;
}

// Step intervals (in hours) for searching sign ingress/egress
const PLANET_STEP_HOURS: Record<PlanetName, number> = {
  Moon: 2, // Moon moves ~13.2 deg/day (~2.25 days per sign)
  Sun: 24, // Sun moves ~1 deg/day (~30 days per sign)
  Mercury: 12, // Fast / variable (~20-30 days per sign)
  Venus: 18, // ~25-35 days per sign
  Mars: 24, // ~45-60 days per sign
  Jupiter: 72, // ~1 year per sign
  Saturn: 120, // ~2.5 years per sign
  Rahu: 96, // ~1.5 years per sign (retrograde)
  Ketu: 96, // ~1.5 years per sign (retrograde)
  Ascendant: 1
};

// Max search limits (in days) backward and forward
const MAX_SEARCH_DAYS: Record<PlanetName, number> = {
  Moon: 5,
  Sun: 45,
  Mercury: 90,
  Venus: 90,
  Mars: 120,
  Jupiter: 500,
  Saturn: 1200,
  Rahu: 700,
  Ketu: 700,
  Ascendant: 1
};

// Deterministic in-memory cache for expensive transit period astronomical calculations
const transitPeriodCache = new Map<string, PlanetaryTransitPeriod>();
const allTransitPeriodsCache = new Map<string, Record<PlanetName, PlanetaryTransitPeriod>>();

/**
 * Accurately finds the transit start (ingress) and end (egress) dates
 * for a planet in its current sign around a target date.
 */
export function calculatePlanetaryTransitPeriod(
  planet: PlanetName,
  targetDate: Date = new Date(),
  context?: CalculationContext | NodeConvention
): PlanetaryTransitPeriod {
  const nodeConvention: NodeConvention =
    typeof context === 'string' ? context : (context?.nodeConvention || 'mean');

  // Stable hour-resolution cache key (astronomical sign ingress/egress is invariant within the same hour)
  const cacheKey = `${planet}_${targetDate.toISOString().slice(0, 13)}_${nodeConvention}`;
  if (transitPeriodCache.has(cacheKey)) {
    return transitPeriodCache.get(cacheKey)!;
  }

  const currentEphem = calculateLivePlanetaryEphemeris(targetDate, { nodeConvention });
  const planetPos = currentEphem.find((p) => p.planet === planet);
  
  const currentSignIndex = planetPos ? planetPos.signIndex : 0;
  const currentSign = planetPos ? planetPos.sign : 'Aries';
  const isRetrograde = planetPos ? planetPos.isRetrograde : false;
  const degreeFormatted = planetPos ? planetPos.formattedDegree : '00° 00\'';

  const stepMs = (PLANET_STEP_HOURS[planet] || 24) * 3600 * 1000;
  const maxSearchMs = (MAX_SEARCH_DAYS[planet] || 60) * 86400 * 1000;
  const targetMs = targetDate.getTime();

  // Helper to check planet's sign at a given time ms
  const getSignAt = (ms: number): number => {
    const ephem = calculateLivePlanetaryEphemeris(new Date(ms), { nodeConvention });
    const p = ephem.find((item) => item.planet === planet);
    return p ? p.signIndex : currentSignIndex;
  };

  // 1. Search Backward for Ingress (Start Date)
  let tStart = targetMs;
  let tPrev = targetMs;
  while (tStart > targetMs - maxSearchMs) {
    const nextT = tStart - stepMs;
    const signAtT = getSignAt(nextT);
    if (signAtT !== currentSignIndex) {
      // Ingress occurred between nextT and tStart -> binary search
      let low = nextT;
      let high = tStart;
      while (high - low > 300000) { // 5 minutes precision
        const mid = (low + high) / 2;
        if (getSignAt(mid) === currentSignIndex) {
          high = mid;
        } else {
          low = mid;
        }
      }
      tStart = high;
      break;
    }
    tStart = nextT;
  }

  // 2. Search Forward for Egress (End Date)
  let tEnd = targetMs;
  while (tEnd < targetMs + maxSearchMs) {
    const nextT = tEnd + stepMs;
    const signAtT = getSignAt(nextT);
    if (signAtT !== currentSignIndex) {
      // Egress occurred between tEnd and nextT -> binary search
      let low = tEnd;
      let high = nextT;
      while (high - low > 300000) { // 5 minutes precision
        const mid = (low + high) / 2;
        if (getSignAt(mid) === currentSignIndex) {
          low = mid;
        } else {
          high = mid;
        }
      }
      tEnd = low;
      break;
    }
    tEnd = nextT;
  }

  const startDate = new Date(tStart);
  const endDate = new Date(tEnd);

  const totalDurationMs = Math.max(86400000, tEnd - tStart);
  const durationDays = Math.round(totalDurationMs / 86400000);
  
  const elapsedMs = Math.max(0, targetMs - tStart);
  const daysElapsed = Math.round(elapsedMs / 86400000);
  const daysRemaining = Math.max(0, durationDays - daysElapsed);
  const progressPercentage = Math.min(100, Math.max(0, Math.round((elapsedMs / totalDurationMs) * 100)));

  // Format Duration string
  let durationFormatted = `${durationDays} Days`;
  if (durationDays >= 365) {
    const yrs = Math.floor(durationDays / 365);
    const mos = Math.round((durationDays % 365) / 30.4);
    durationFormatted = yrs > 0 ? `${yrs}y ${mos > 0 ? `${mos}m` : ''}`.trim() : `${mos}m`;
  } else if (durationDays >= 45) {
    const mos = Math.round(durationDays / 30.4);
    durationFormatted = `${mos} Months`;
  } else if (planet === 'Moon') {
    const totalHours = Math.round(totalDurationMs / 3600000);
    durationFormatted = `${totalHours} Hours (~${(totalHours / 24).toFixed(1)} Days)`;
  }

  // 3. Determine Next Sign (Upcoming Rashi) and Next Period (From - To)
  const nextTargetMs = tEnd + 3600000; // 1 hour after egress
  const nextSignIndex = getSignAt(nextTargetMs);
  const nextSign = ZODIAC_SIGNS[nextSignIndex] || (ZODIAC_SIGNS[(currentSignIndex + (isRetrograde ? 11 : 1)) % 12]);
  const nextIngressDate = endDate;
  const nextIngressDateFormatted = formatDate(endDate);
  const daysUntilNextIngress = daysRemaining;

  // Search forward for when it exits the next sign (Next Period End / To Date)
  let tNextEnd = nextTargetMs;
  while (tNextEnd < nextTargetMs + maxSearchMs * 1.5) {
    const nextT = tNextEnd + stepMs;
    const signAtT = getSignAt(nextT);
    if (signAtT !== nextSignIndex) {
      let low = tNextEnd;
      let high = nextT;
      while (high - low > 300000) { // 5 minutes precision
        const mid = (low + high) / 2;
        if (getSignAt(mid) === nextSignIndex) {
          high = mid;
        } else {
          low = mid;
        }
      }
      tNextEnd = low;
      break;
    }
    tNextEnd = nextT;
  }

  const nextPeriodStartDate = endDate;
  const nextPeriodStartDateFormatted = formatDate(nextPeriodStartDate);
  const nextPeriodEndDate = new Date(tNextEnd);
  const nextPeriodEndDateFormatted = formatDate(nextPeriodEndDate);
  const nextDurationMs = Math.max(86400000, tNextEnd - tEnd);
  const nextDurationDays = Math.round(nextDurationMs / 86400000);
  let nextDurationFormatted = `${nextDurationDays} Days`;
  if (nextDurationDays >= 365) {
    const yrs = Math.floor(nextDurationDays / 365);
    const mos = Math.round((nextDurationDays % 365) / 30.4);
    nextDurationFormatted = yrs > 0 ? `${yrs}y ${mos > 0 ? `${mos}m` : ''}`.trim() : `${mos}m`;
  } else if (nextDurationDays >= 45) {
    const mos = Math.round(nextDurationDays / 30.4);
    nextDurationFormatted = `${mos} Months`;
  } else if (planet === 'Moon') {
    const totalHours = Math.round(nextDurationMs / 3600000);
    nextDurationFormatted = `${totalHours} Hours (~${(totalHours / 24).toFixed(1)} Days)`;
  }

  const result: PlanetaryTransitPeriod = {
    planet,
    sign: currentSign,
    signIndex: currentSignIndex,
    startDate,
    endDate,
    startDateFormatted: formatDate(startDate),
    endDateFormatted: formatDate(endDate),
    durationDays,
    durationFormatted,
    daysElapsed,
    daysRemaining,
    progressPercentage,
    isRetrograde,
    degreeFormatted,
    nextSign,
    nextSignIndex,
    nextIngressDate,
    nextIngressDateFormatted,
    nextPeriodStartDate,
    nextPeriodStartDateFormatted,
    nextPeriodEndDate,
    nextPeriodEndDateFormatted,
    nextDurationDays,
    nextDurationFormatted,
    daysUntilNextIngress
  };

  transitPeriodCache.set(cacheKey, result);
  return result;
}

/**
 * Calculates transit periods for all 9 major Vedic grahas for a given date.
 * Uses memoized results to prevent expensive duplicate binary searches on re-renders.
 */
export function calculateAllPlanetaryTransitPeriods(
  targetDate: Date = new Date(),
  context?: CalculationContext | NodeConvention
): Record<PlanetName, PlanetaryTransitPeriod> {
  const nodeConvention: NodeConvention =
    typeof context === 'string' ? context : (context?.nodeConvention || 'mean');
  const allCacheKey = `ALL_${targetDate.toISOString().slice(0, 13)}_${nodeConvention}`;
  if (allTransitPeriodsCache.has(allCacheKey)) {
    return allTransitPeriodsCache.get(allCacheKey)!;
  }

  const planets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
  const result: Partial<Record<PlanetName, PlanetaryTransitPeriod>> = {};

  for (const p of planets) {
    result[p] = calculatePlanetaryTransitPeriod(p, targetDate, nodeConvention);
  }

  const completeResult = result as Record<PlanetName, PlanetaryTransitPeriod>;
  allTransitPeriodsCache.set(allCacheKey, completeResult);
  return completeResult;
}

export interface UpcomingTransitIngress {
  planet: PlanetName;
  ingressDate: Date;
  ingressDateFormatted: string;
  daysUntilIngress: number;
  durationFormatted: string;
  durationDays: number;
  fromSign: ZodiacSign;
  toSign: ZodiacSign;
  isSlowPlanet: boolean;
  isOccupantNow: boolean;
}

export interface HouseNextTransitInfo {
  targetSign: ZodiacSign;
  targetSignIndex: number;
  immediateNext: UpcomingTransitIngress | null;
  majorNext: UpcomingTransitIngress | null;
  upcomingIngresses: UpcomingTransitIngress[];
}

/**
 * Calculates which planets will transit/ingress into a target Rashi next,
 * providing both immediate next ingress and upcoming macro-cycle (slow planet) ingresses.
 */
export function calculateNextTransitsForSign(
  targetSignIndex: number,
  targetDate: Date = new Date(),
  context?: CalculationContext | NodeConvention
): HouseNextTransitInfo {
  const nodeConvention: NodeConvention =
    typeof context === 'string' ? context : (context?.nodeConvention || 'mean');
  const targetSign = ZODIAC_SIGNS[targetSignIndex % 12];
  const targetMs = targetDate.getTime();
  const allPlanets: PlanetName[] = ['Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Rahu', 'Ketu'];

  // Helper to check planet's sign at a given time ms
  const getSignAt = (planet: PlanetName, ms: number): number => {
    const ephem = calculateLivePlanetaryEphemeris(new Date(ms), { nodeConvention });
    const p = ephem.find((item) => item.planet === planet);
    return p ? p.signIndex : 0;
  };

  const MAX_AHEAD_DAYS: Record<PlanetName, number> = {
    Moon: 32,
    Sun: 380,
    Mercury: 380,
    Venus: 380,
    Mars: 750,
    Jupiter: 4600,
    Saturn: 11500,
    Rahu: 7000,
    Ketu: 7000,
    Ascendant: 1
  };

  const upcomingList: UpcomingTransitIngress[] = [];

  for (const planet of allPlanets) {
    const currSignIdx = getSignAt(planet, targetMs);
    const stepMs = (PLANET_STEP_HOURS[planet] || 24) * 3600 * 1000;
    const maxSearchMs = (MAX_AHEAD_DAYS[planet] || 380) * 86400 * 1000;

    let t = targetMs;
    const isCurrentlyOccupant = currSignIdx === targetSignIndex;

    // If already in target sign, first advance past egress
    if (isCurrentlyOccupant) {
      while (t < targetMs + maxSearchMs) {
        t += stepMs;
        if (getSignAt(planet, t) !== targetSignIndex) {
          break;
        }
      }
    }

    // Now search forward for the next ingress into targetSignIndex
    let foundIngress = false;
    let ingressStart = t;

    while (t < targetMs + maxSearchMs) {
      const nextT = t + stepMs;
      const signAtNextT = getSignAt(planet, nextT);
      const signAtT = getSignAt(planet, t);

      if (signAtNextT === targetSignIndex && signAtT !== targetSignIndex) {
        // Binary search for exact entry
        let low = t;
        let high = nextT;
        while (high - low > 300000) { // 5 min precision
          const mid = (low + high) / 2;
          if (getSignAt(planet, mid) === targetSignIndex) {
            high = mid;
          } else {
            low = mid;
          }
        }
        ingressStart = high;
        foundIngress = true;
        break;
      }
      t = nextT;
    }

    if (foundIngress) {
      const ingressDate = new Date(ingressStart);
      const daysUntilIngress = Math.max(0, Math.round((ingressStart - targetMs) / 86400000));
      const fromSignIndex = getSignAt(planet, ingressStart - 3600000);
      const fromSign = ZODIAC_SIGNS[fromSignIndex] || 'Aries';

      // Find egress from targetSign to get duration
      let egressT = ingressStart + stepMs;
      while (egressT < ingressStart + maxSearchMs) {
        const nextET = egressT + stepMs;
        if (getSignAt(planet, nextET) !== targetSignIndex) {
          let low = egressT;
          let high = nextET;
          while (high - low > 300000) {
            const mid = (low + high) / 2;
            if (getSignAt(planet, mid) === targetSignIndex) {
              low = mid;
            } else {
              high = mid;
            }
          }
          egressT = low;
          break;
        }
        egressT = nextET;
      }

      const durMs = Math.max(86400000, egressT - ingressStart);
      const durationDays = Math.round(durMs / 86400000);
      let durationFormatted = `${durationDays} Days`;
      if (durationDays >= 365) {
        const yrs = Math.floor(durationDays / 365);
        const mos = Math.round((durationDays % 365) / 30.4);
        durationFormatted = yrs > 0 ? `${yrs}y ${mos > 0 ? `${mos}m` : ''}`.trim() : `${mos}m`;
      } else if (durationDays >= 45) {
        const mos = Math.round(durationDays / 30.4);
        durationFormatted = `${mos} Months`;
      } else if (planet === 'Moon') {
        const totalHours = Math.round(durMs / 3600000);
        durationFormatted = `${totalHours}h (~${(totalHours / 24).toFixed(1)}d)`;
      }

      const isSlowPlanet = ['Jupiter', 'Saturn', 'Rahu', 'Ketu', 'Mars'].includes(planet);

      upcomingList.push({
        planet,
        ingressDate,
        ingressDateFormatted: formatDate(ingressDate),
        daysUntilIngress,
        durationDays,
        durationFormatted,
        fromSign,
        toSign: targetSign,
        isSlowPlanet,
        isOccupantNow: isCurrentlyOccupant
      });
    }
  }

  // Sort chronologically by days until ingress
  upcomingList.sort((a, b) => a.daysUntilIngress - b.daysUntilIngress);

  const immediateNext = upcomingList.length > 0 ? upcomingList[0] : null;
  const majorNext = upcomingList.find((item) => item.isSlowPlanet) || null;

  return {
    targetSign,
    targetSignIndex,
    immediateNext,
    majorNext,
    upcomingIngresses: upcomingList
  };
}

